import { ApiError } from '@bookora/ui';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

/**
 * Saving a response body to the user's disk.
 *
 * Two callers with the same problem: the accounting workbook and the
 * watermarked clinical photograph. Both go through the RAW axios instance
 * rather than the mutator — the mutator unwraps a `{ data }` JSON envelope, and
 * neither an `.xlsx` nor a JPEG is JSON — and both then need the same anchor
 * dance to turn a `Blob` into a file. It lived in `features/accounting/` until
 * the photo download needed it too.
 */

/**
 * Hand a blob to the browser as a download.
 *
 * The `setTimeout` is not superstition: revoking the object URL synchronously
 * cancels the download in Safari, and this product's clinicians are largely on
 * iPads. A tick is enough.
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}

/**
 * The filename the server chose, out of a `Content-Disposition` header.
 *
 * The backend sends RFC 5987: a percent-encoded UTF-8 `filename*` carrying the
 * real Persian name, plus a plain ASCII `filename` for agents that do not
 * understand the former. Prefer `filename*` — taking the ASCII fallback would
 * hand «عکس-قبل-۱۴۰۴.jpg» to the clinician as `photo-1.jpg`.
 *
 * Returns `null` rather than guessing when the header is absent or unparsable,
 * so the caller decides what an unnamed file is called.
 */
export function filenameFromDisposition(disposition: string | undefined): string | null {
  if (!disposition) return null;

  const extended = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(disposition);
  if (extended?.[1]) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      // A malformed percent-sequence is not worth failing a download over;
      // fall through to the ASCII half.
    }
  }

  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(disposition);
  return plain?.[1]?.trim() || null;
}

/* -------------------------------------------------------------------------- */
/* Asking for a file, and reading the server's refusal when it says no        */
/* -------------------------------------------------------------------------- */

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  «پاسخ سرور قابل خواندن نبود» WAS EVERY FAILED DOWNLOAD, WHATEVER WENT WRONG
 * ════════════════════════════════════════════════════════════════════════════
 *
 * The shared client builds a real `ApiError` only when `err.response.data` is
 * an object with a string `code` (`packages/ui/src/api/client.ts`). With
 * `responseType: 'blob'` axios hands it a `Blob`, so every failure — a 403
 * `TWO_FACTOR_REQUIRED` on a finance export, a 404, a 500 — fell through to
 * `TRANSPORT_ERROR_CODES.MALFORMED` and the panel told the user the server's
 * answer was unreadable. The server's answer was a perfectly readable Persian
 * sentence naming the second factor it wanted; it was just wrapped in a Blob.
 *
 * So a download resolves its own non-2xx responses, reads the JSON body out of
 * the blob and throws the `ApiError` the server actually sent.
 *
 * ── WHY 401 IS DELIBERATELY EXCLUDED ────────────────────────────────────────
 *
 * 401 is the shared interceptor's refresh-and-retry path. Resolving it here
 * would skip the refresh and turn an expired access token into a failed export
 * — and, worse, hide the session expiry the auth layer needs to broadcast.
 */
export function isBlobErrorStatus(status: number): boolean {
  return status >= 400 && status !== 401;
}

/** The server's own error envelope, recovered from a blob body. */
export async function apiErrorFromBlob(
  status: number,
  body: Blob | undefined,
): Promise<ApiError> {
  const text = await blobText(body);
  if (text !== '') {
    try {
      const parsed: unknown = JSON.parse(text);
      if (parsed && typeof parsed === 'object') {
        const envelope = parsed as {
          statusCode?: number;
          code?: string;
          message?: string;
          details?: unknown;
          requestId?: string;
        };
        return new ApiError({
          statusCode: envelope.statusCode ?? status,
          code: envelope.code,
          message: envelope.message,
          details: envelope.details,
          requestId: envelope.requestId,
        });
      }
    } catch {
      // Not JSON — a proxy's HTML error page, say. Fall through to the status.
    }
  }
  // No body to read: still an HTTP status, which `errorMessageFa` can speak to,
  // and never the "unreadable response" copy.
  return new ApiError({ statusCode: status });
}

/**
 * A blob's body as text.
 *
 * `Blob.text()` where it exists — every browser this product runs in — and
 * `FileReader` otherwise. jsdom 24 ships a `Blob` with no `text()`, so without
 * the fallback this whole path would be untestable, which is how it came to be
 * wrong in the first place.
 */
async function blobText(body: Blob | undefined): Promise<string> {
  if (!body) return '';
  try {
    if (typeof body.text === 'function') return (await body.text()).trim();
    return await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        // `readAsText` always yields a string; the union is `FileReader`'s.
        resolve(typeof reader.result === 'string' ? reader.result.trim() : '');
      };
      reader.onerror = () => resolve('');
      reader.readAsText(body);
    });
  } catch {
    return '';
  }
}

/**
 * `GET` a file. Resolves with the axios response on success and throws the
 * server's own `ApiError` on failure.
 */
export async function requestBlob(
  client: AxiosInstance,
  url: string,
  config: AxiosRequestConfig = {},
): Promise<AxiosResponse<Blob>> {
  const response = await client.get<Blob>(url, {
    ...config,
    responseType: 'blob',
    validateStatus: (status) => !isBlobErrorStatus(status),
  });
  if (isBlobErrorStatus(response.status)) {
    throw await apiErrorFromBlob(response.status, response.data);
  }
  return response;
}
